import React, { useState, useEffect } from 'react';
import Cell from './Cell';
import GlassFront from './GlassFront';
import GlassBack from './GlassBack';
import EVAFront from './EVAFront';
import EVABack from './EVABack';
import Flux from './Flux';
import Interconnector from './Interconnector';
import StringConnector from './StringConnector';
import CellFixingType from './CellFixingType';
import Frame from './Frame';
import JunctionBox from './JunctionBox';
import AdhesiveSealant from './AdhesiveSealant';
import PottingMaterial from './PottingMaterial';
import { useNavigate } from 'react-router-dom';

const BomMain = () => {
    const [activeTab, setActiveTab] = useState('Cell');
    const navigate = useNavigate();

    // Global fields
    const [globalFields, setGlobalFields] = useState({
        date: '',
        shift: '',
        inspectorName: '',
    });

    // Initialize formData from sessionStorage
    const [formData, setFormData] = useState(() => {
        const savedData = sessionStorage.getItem('formData');
        return savedData
            ? JSON.parse(savedData)
            : {
                  Cell: {},
                  'Glass(Front)': {},
                  'Glass(Back)': {},
                  'EVA(Front)': {},
                  'EVA(Back)': {},
                  Flux: {},
                  Interconnector: {},
                  'String Connector': {},
                  'Cell Fixing Type': {},
                  Frame: {},
                  'Junction Box': {},
                  'Adhesive(Sealant)': {},
                  'Potting Material': {},
              };
    });

    // Save formData to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('formData', JSON.stringify(formData));
    }, [formData]);

    // Handle global field changes
    const handleGlobalFieldChange = (e) => {
        const { name, value } = e.target;
        setGlobalFields((prevFields) => ({
            ...prevFields,
            [name]: value,
        }));
    };

    // Handle form data changes
    const handleFormDataChange = (tab, updatedData) => {
        setFormData((prevData) => ({
            ...prevData,
            [tab]: updatedData,
        }));
    };

    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        setGlobalFields({
            date: inspectionDate || '',
            shift: inspectionShift || '',
            inspectorName: inspectorName || '',
        });
    }, []);

    const goToNextForm = () => {
        navigate('/inspections/section2/form4');
    };

    const goToPreviousForm = () => {
        navigate('/inspections/section1/form2');
    };

    const renderActiveTab = () => {
        const props = {
            globalFields,
            formData: formData[activeTab],
            onFormDataChange: (updatedData) => handleFormDataChange(activeTab, updatedData),
        };

        switch (activeTab) {
            case 'Cell':
                return <Cell {...props} />;
            case 'Glass(Front)':
                return <GlassFront {...props} />;
            case 'Glass(Back)':
                return <GlassBack {...props} />;
            case 'EVA(Front)':
                return <EVAFront {...props} />;
            case 'EVA(Back)':
                return <EVABack {...props} />;
            case 'Flux':
                return <Flux {...props} />;
            case 'Interconnector':
                return <Interconnector {...props} />;
            case 'String Connector':
                return <StringConnector {...props} />;
            case 'Cell Fixing Type':
                return <CellFixingType {...props} />;
            case 'Frame':
                return <Frame {...props} />;
            case 'Junction Box':
                return <JunctionBox {...props} />;
            case 'Adhesive(Sealant)':
                return <AdhesiveSealant {...props} />;
            case 'Potting Material':
                return <PottingMaterial {...props} />;
            default:
                return <Cell {...props} />;
        }
    };

    const tabItems = [
        'Cell',
        'Glass(Front)',
        'Glass(Back)',
        'EVA(Front)',
        'EVA(Back)',
        'Flux',
        'Interconnector',
        'String Connector',
        'Cell Fixing Type',
        'Frame',
        'Junction Box',
        'Adhesive(Sealant)',
        'Potting Material',
    ];

    return (
        <div className="flex max-w-7xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
            {/* Sidebar for Tabs */}
            <div className="w-1/4 bg-[#424752] text-white p-4">
                <h3 className="text-lg font-semibold mb-4">BOM Tabs</h3>
                <div className="space-y-2">
                    {tabItems.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`w-full text-gray-300 text-left p-3 rounded-lg transition-colors duration-300 ${
                                activeTab === tab
                                    ? 'text-gray-500 bg-white font-bold'
                                    : 'hover:bg-gray-200 hover:text-black'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>
            {/* Main Content */}
            <div className="w-3/4 p-6">
                {/* Global Fields */}
                <div className="bg-gray-200 p-4 rounded-lg mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-700">Global Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Date</label>
                            <input
                                type="date"
                                name="date"
                                value={globalFields.date}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Shift</label>
                            <input
                                type="text"
                                name="shift"
                                placeholder="Shift"
                                value={globalFields.shift}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Inspector Name</label>
                            <input
                                type="text"
                                name="inspectorName"
                                placeholder="Inspector Name"
                                value={globalFields.inspectorName}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                    </div>
                </div>
                {/* Active Tab Content */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-inner">
                    {renderActiveTab()}
                </div>
                <div className="flex justify-between mt-6">
                    <button
                        onClick={goToPreviousForm}
                        className="bg-[#424752] text-white w-[6rem] px-4 py-2 rounded-lg hover:bg-gray-500"
                    >
                        Previous
                    </button>
                    <button
                        onClick={goToNextForm}
                        className="bg-[#424752] text-white w-[6rem] px-4 py-2 rounded-lg hover:bg-gray-500"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BomMain;